import { Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { useCurrency, CURRENCIES } from "@/contexts/CurrencyContext";
import { useLanguage, LANGUAGES } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { usePrivacyPreferences } from "@/contexts/PrivacyPreferencesContext";
import { useState } from "react";
import { useKlaviyoSubscribe } from "@/hooks/use-klaviyo-subscribe";

const Footer = () => {
  const { currency, setCurrency } = useCurrency();
  const { language, setLanguage, t } = useLanguage();
  const { openPreferences } = usePrivacyPreferences();
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const currentCurrency = CURRENCIES.find((c) => c.code === currency);
  const { email, setEmail, submit, loading, message, isSuccess, isError } =
    useKlaviyoSubscribe({
      source: "footer_private_list",
      signupLocation: "footer",
      formName: "Join the Private List",
    });

  const navRef = useScrollReveal<HTMLDivElement>({ preset: "footer" });
  const newsletterRef = useScrollReveal<HTMLDivElement>({ preset: "footer" });
  const legalRef = useScrollReveal<HTMLDivElement>({ preset: "footer" });

  const navGroups = [
    {
      heading: t("footer.shop"),
      links: [
        { label: t("nav.shopWatches"), to: "/archive" },
        { label: t("nav.shopJewelry"), to: "/archive" },
        { label: t("nav.shopAccessories"), to: "/archive" },
        { label: t("nav.sellYourWatch"), to: null },
      ],
    },
    {
      heading: t("footer.services"),
      links: [
        { label: t("nav.faq"), to: null },
        { label: t("nav.shipping"), to: "/shipping-returns" },
        { label: t("nav.returns"), to: "/shipping-returns" },
        { label: t("nav.buyersProtection"), to: null },
      ],
    },
    {
      heading: "MOTAI",
      links: [
        { label: t("nav.articles"), to: null },
        { label: t("nav.about"), to: null },
        { label: t("nav.emailList"), to: "/email-list" },
        { label: t("nav.contactUs"), to: null },
      ],
    },
  ];

  const legalLinksData = [
    { label: t("footer.termsConditions"), to: "/terms" },
    { label: t("footer.privacyPolicy"), to: "/privacy" },
    { label: t("footer.refundPolicy"), to: "/refund-policy" },
    { label: t("footer.shippingReturns"), to: "/shipping-returns" },
    { label: t("footer.cookiePolicy"), to: "/cookies" },
  ];

  return (
    <footer className="bg-card text-foreground">
      {/* ─── Navigation columns ─── */}
      <div className="container-site pt-12 pb-8 md:pt-14 md:pb-10 lg:pt-16 lg:pb-12">
        <div className="container-content">
          <div ref={navRef} className="flex flex-col items-center gap-10 md:gap-0 md:grid md:grid-cols-3 md:gap-x-10 lg:gap-x-16 text-center">
            {navGroups.map((group) => (
              <div key={group.heading}>
                <h3 className="heading-x-small text-foreground mb-4 md:mb-5">
                  {group.heading}
                </h3>
                <ul className="space-y-3">
                  {group.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <Link to={link.to} className="body-small text-foreground/70 hover:text-foreground transition-colors">
                          {link.label}
                        </Link>
                      ) : (
                        <span className="body-small text-foreground/70 cursor-default">
                          {link.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Newsletter signup ─── */}
      <div className="container-site pb-8 md:pb-10 lg:pb-12">
        <div className="container-content">
          <div ref={newsletterRef} className="max-w-md mx-auto text-center">
            <h3 className="heading-x-small text-foreground mb-2">
              {t("newsletter.title")}
            </h3>
            <p className="body-small text-foreground/70 mb-5 md:mb-6">
              {t("newsletter.subtitle")}
            </p>
            <form onSubmit={submit} className="flex items-stretch gap-2" noValidate>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder={t("newsletter.placeholder")}
                className="m-0 flex-1 h-11 bg-input border border-border px-4 body-small text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={loading}
                className="m-0 h-11 px-4 bg-accent text-accent-foreground hover:bg-accent-hover active:bg-accent-active transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "…" : t("newsletter.subscribe")}
              </button>
            </form>
            {message && (
              <p
                role={isError ? "alert" : "status"}
                className={`mt-3 text-meta ${isSuccess ? "text-foreground" : "text-muted-foreground"}`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Social icons ─── */}
      <div className="container-site pb-6 md:pb-8">
        <div className="container-content flex justify-center gap-6">
          <a href="#" aria-label="Instagram" className="text-foreground/60 hover:text-foreground transition-colors">
            <Instagram size={18} />
          </a>
          <a href="#" aria-label="Facebook" className="text-foreground/60 hover:text-foreground transition-colors">
            <Facebook size={18} />
          </a>
          <a href="#" aria-label="X" className="text-foreground/60 hover:text-foreground transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        </div>
      </div>

      {/* ─── Legal / policy links ─── */}
      <div className="container-site pb-4 md:pb-5">
        <div className="container-content">
          <div ref={legalRef} className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-center">
            {legalLinksData.map((link) => (
              link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="body-x-small text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href="#"
                  className="body-x-small text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
                >
                  {link.label}
                </a>
              )
            ))}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); openPreferences(); }}
              className="body-x-small text-foreground/60 hover:text-foreground transition-colors cursor-pointer"
            >
              Your Privacy Choices
            </a>
          </div>
        </div>
      </div>

      {/* ─── Language + Currency selectors ─── */}
      <div className="container-site pb-3 md:pb-4">
        <div
          className="container-content flex items-center justify-center gap-4 text-meta relative"
          style={{ fontFamily: '"Motai Gallery", serif' }}
        >
          {/* Language selector */}
          <div className="relative">
            <button
              onClick={() => setLanguageOpen((v) => !v)}
              className="flex items-center gap-1.5 px-2 py-1 text-foreground/60 hover:text-foreground transition-colors"
            >
              {language}
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                className={`transition-transform duration-200 ${languageOpen ? "rotate-180" : ""}`}
              >
                <path d="M2 4l3 3 3-3" />
              </svg>
            </button>

            {languageOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLanguageOpen(false)} />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-popover border border-border py-1 min-w-[120px] max-h-[calc(8*2.25rem)] overflow-y-auto">
                  {LANGUAGES.filter((l) => l.code !== language).map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        setLanguage(l.code);
                        setLanguageOpen(false);
                      }}
                      className="popout-list__option w-full px-4 py-2 text-left text-foreground/70 hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <span className="text-foreground/30">|</span>

          {/* Currency selector */}
          <div className="relative">
            <button
              onClick={() => setCurrencyOpen((v) => !v)}
              className="flex items-center gap-1.5 px-2 py-1 text-foreground/60 hover:text-foreground transition-colors"
            >
              {currentCurrency?.code} {currentCurrency?.symbol}
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                className={`transition-transform duration-200 ${currencyOpen ? "rotate-180" : ""}`}
              >
                <path d="M2 4l3 3 3-3" />
              </svg>
            </button>

            {currencyOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setCurrencyOpen(false)} />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-popover border border-border py-1 min-w-[120px] max-h-[calc(8*2.25rem)] overflow-y-auto">
                  {CURRENCIES.filter((c) => c.code !== currency).map((c) => (
                    <button
                      key={c.code}
                      onClick={() => {
                        setCurrency(c.code);
                        setCurrencyOpen(false);
                      }}
                      className="popout-list__option w-full px-4 py-2 text-left text-foreground/70 hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {c.code} {c.symbol}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Copyright ─── */}
      <div className="container-site pb-6 md:pb-8 lg:pb-10">
        <div className="container-content pt-4 md:pt-5 relative">
          <p className="text-meta text-foreground/60 text-center">
            © {new Date().getFullYear()} MOTAI. {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
